import axios from 'axios';

function getAuthToken() {
    return JSON.parse(localStorage.getItem('token'))?.token;
}

function apiConfig() {
    const token = getAuthToken();
    return {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    };
}

/**
 * Upload a File to S3 via backend-managed multipart upload.
 *
 * Backend contract:
 * - init:    POST -> { uploadId, key, bucket, partSizeBytes }
 * - partUrl: POST -> { url }
 * - complete POST -> { fileUrl?, key, bucket }
 * - abort   POST -> 200
 */
export async function uploadMultipartToS3({
    apiBaseUrl,
    initPath,
    partUrlPath,
    completePath,
    abortPath,
    projectId,
    file,
    extraInitPayload = {},
    extraCompletePayload = {},
    onStateChange,
    onProgress,
}) {
    const filename = file?.name;
    const contentType = file?.type;
    const sizeBytes = file?.size;

    let uploadId = null;
    let key = null;

    const emitState = (state) => onStateChange && onStateChange(state);
    const emitProgress = (pct) => onProgress && onProgress(pct);

    try {
        emitState('preparing');

        const initResp = await axios.post(
            `${apiBaseUrl}${initPath}/${projectId}`,
            { filename, contentType, sizeBytes, ...extraInitPayload },
            apiConfig()
        );

        uploadId = initResp.data.uploadId;
        key = initResp.data.key;
        const partSizeBytes = initResp.data.partSizeBytes || 8 * 1024 * 1024;

        const totalParts = Math.ceil(file.size / partSizeBytes);
        const parts = [];

        emitState('uploading');
        emitProgress(0);

        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
            const start = (partNumber - 1) * partSizeBytes;
            const end = Math.min(partNumber * partSizeBytes, file.size);
            const chunk = file.slice(start, end);

            const presignResp = await axios.post(
                `${apiBaseUrl}${partUrlPath}/${projectId}`,
                { key, uploadId, partNumber },
                apiConfig()
            );

            const url = presignResp.data.url;

            const putResp = await axios.put(url, chunk, {
                headers: {
                    // S3 accepts this header; signature does not depend on it by default
                    'Content-Type': contentType || 'application/octet-stream',
                },
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    const uploadedSoFar = start + evt.loaded;
                    const pct = Math.min(100, Math.floor((uploadedSoFar / file.size) * 100));
                    emitProgress(pct);
                },
            });

            const etag = putResp.headers?.etag;
            if (!etag) {
                throw new Error('Missing ETag header from S3 response. Ensure the S3 bucket CORS exposes "ETag".');
            }
            parts.push({ PartNumber: partNumber, ETag: etag });
        }

        emitState('finalizing');

        const completeResp = await axios.post(
            `${apiBaseUrl}${completePath}/${projectId}`,
            {
                key,
                uploadId,
                parts,
                filename,
                contentType,
                sizeBytes,
                ...extraCompletePayload,
            },
            apiConfig()
        );

        emitState('success');
        emitProgress(100);

        return completeResp.data;
    } catch (err) {
        emitState('error');
        try {
            if (uploadId && key) {
                await axios.post(
                    `${apiBaseUrl}${abortPath}/${projectId}`,
                    { key, uploadId },
                    apiConfig()
                );
            }
        } catch (_) {
            // Best-effort abort
        }
        throw err;
    }
}

